/**
 * =============================================================================
 * Página: /afiliado/materiais
 * Versão: 5.0.0 (Deduplicação + texto expansível + cards mais largos)
 *
 * Descrição:
 *   Galeria premium de materiais. Cards com imagem em destaque, badge de tipo
 *   sobreposto e ações principais.
 *   v5.0.0:
 *     - Remove duplicidade por programa: cada material aparece uma vez, com
 *       chips dos programas vinculados.
 *     - Texto da descrição expande ao clicar para leitura completa.
 *     - Botão de copiar em formato somente ícone.
 *     - Cards 30% mais largos horizontalmente (grid com menos colunas).
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import {
  Image as ImageIcon,
  FileText,
  Video,
  Copy,
  Check,
  Download,
  Share2,
  Maximize2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  useAffiliateMaterials,
  type AffiliateMaterialRow,
} from '@/hooks/useAffiliateMaterials';
import { AffiliatePageHeader } from '@/components/affiliate/shared/AffiliatePageHeader';
import { AffiliateEmptyState } from '@/components/affiliate/shared/AffiliateEmptyState';
// Preview universal de mídias (imagem/vídeo/documento)
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
// v5.0.0 — descrição do material pode conter HTML (editor rico do admin).
// Exibição usa HTML sanitizado; WhatsApp/caption usam texto puro.
import { sanitizeHtml, stripHtml } from '@/lib/sanitize';


function isImage(m: AffiliateMaterialRow): boolean {
  if (m.mime_type?.startsWith('image/')) return true;
  if (m.material_type === 'image' || m.material_type === 'banner') return true;
  return false;
}

function isVideo(m: AffiliateMaterialRow): boolean {
  if (m.mime_type?.startsWith('video/')) return true;
  return m.material_type === 'video';
}

function typeIcon(m: AffiliateMaterialRow) {
  if (isImage(m)) return <ImageIcon className="h-3.5 w-3.5" />;
  if (isVideo(m)) return <Video className="h-3.5 w-3.5" />;
  return <FileText className="h-3.5 w-3.5" />;
}

function typeLabel(m: AffiliateMaterialRow): string {
  if (isImage(m)) return 'Imagem';
  if (isVideo(m)) return 'Vídeo';
  if (m.material_type === 'copy' || m.text_content) return 'Texto';
  return m.material_type;
}

export default function AffiliateMaterials() {
  const { data: materials, isLoading, isError, refetch } = useAffiliateMaterials();

  // v5.0.0 — lista única, sem agrupamento por programa.
  const rows = useMemo(() => materials ?? [], [materials]);

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-6 space-y-6 aff-animate-in">
      <AffiliatePageHeader
        icon={<ImageIcon className="h-5 w-5" />}
        title="Materiais"
        subtitle="Banners, vídeos e textos prontos para você divulgar."
      />

      {isLoading && (
        // v5.0.0 — grid reduzido para cards ~30% mais largos horizontalmente.
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-2xl" />
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <div className="aff-card flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
            Não foi possível carregar os materiais.
          </p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {!isLoading && !isError && (rows?.length ?? 0) === 0 && (
        <AffiliateEmptyState
          icon={<ImageIcon className="h-7 w-7" />}
          title="Nenhum material disponível"
          description="Quando o programa publicar banners, vídeos ou textos, eles aparecerão aqui para você usar."
        />
      )}

      {!isLoading && !isError && (rows?.length ?? 0) > 0 && (
        // v5.0.0 — grade única, sem seções por programa.
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {rows.map((m) => (
            <MaterialCard key={m.id} material={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function MaterialCard({ material: m }: { material: AffiliateMaterialRow }) {
  const [copied, setCopied] = useState(false);
  // v5.0.0 — controla o preview interno (lightbox) do material.
  const [previewOpen, setPreviewOpen] = useState(false);
  // v5.0.0 — controla expansão/contração da descrição.
  const [expanded, setExpanded] = useState(false);

  /** Texto puro do material para copiar/compartilhar. */
  const plainText = stripHtml(m.description) || m.text_content?.trim() || m.title;

  /** Tipo da mídia para o lightbox universal. */
  const mediaItem: MediaItem | null = m.file_url
    ? {
        url: m.file_url,
        type: isImage(m) ? 'image' : isVideo(m) ? 'video' : 'document',
        title: m.title,
      }
    : null;

  async function handleCopy() {
    if (!plainText) return;
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      toast.success('Texto copiado.');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  }

  /**
   * Compartilha o material no WhatsApp (app no mobile / WhatsApp Web no
   * desktop). A mensagem usa a descrição do material (fallback: título)
   * e acrescenta o link do arquivo quando existir.
   */
  function handleShareWhatsApp() {
    const partes: string[] = [];
    if (plainText) partes.push(plainText);
    if (m.file_url) partes.push(m.file_url);

    const mensagem = partes.join('\n\n');
    if (!mensagem) {
      toast.error('Nada para compartilhar neste material.');
      return;
    }

    window.open(
      `https://wa.me/?text=${encodeURIComponent(mensagem)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  /** Programas vinculados ao material, truncados para 2 + "+N". */
  function renderProgramChips() {
    if (!m.programs || m.programs.length === 0) return null;

    const visible = m.programs.slice(0, 2);
    const extra = m.programs.length - visible.length;

    return (
      <div className="flex flex-wrap gap-1">
        {visible.map((p) => (
          <span
            key={p.id ?? p.name}
            className="inline-flex items-center rounded-full bg-[hsl(var(--aff-primary)/0.12)] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--aff-primary))]"
            title={p.name}
          >
            {p.name}
          </span>
        ))}
        {extra > 0 && (
          <span
            className="inline-flex items-center rounded-full bg-[hsl(var(--aff-muted)/0.12)] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--aff-muted-foreground))]"
            title={m.programs.slice(2).map((p) => p.name).join(', ')}
          >
            +{extra}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="aff-card overflow-hidden flex flex-col">
      <div
        className={`relative aspect-[4/3] w-full bg-[hsl(var(--aff-surface-2))] ${
          mediaItem ? 'cursor-zoom-in' : ''
        }`}
        onClick={() => mediaItem && setPreviewOpen(true)}
      >
        {isImage(m) && m.file_url ? (
          <img
            src={m.file_url}
            alt={m.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : isVideo(m) && m.file_url ? (
          <video
            src={m.file_url}
            preload="metadata"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center p-3 text-center">
            <p className="line-clamp-4 text-xs text-[hsl(var(--aff-muted-foreground))]">
              {m.text_content || stripHtml(m.description) || 'Sem preview disponível.'}
            </p>
          </div>
        )}
        <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--aff-bg)/0.92)] backdrop-blur px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--aff-foreground))] shadow-sm">
          {typeIcon(m)}
          {typeLabel(m)}
        </span>
      </div>

      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="space-y-1 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-bold leading-tight line-clamp-1 text-[hsl(var(--aff-foreground))]">
              {m.title}
            </h3>
            {/* v5.0.0 — botão de copiar apenas ícone, ao lado do título. */}
            {plainText && (
              <button
                type="button"
                onClick={handleCopy}
                title={copied ? 'Copiado' : 'Copiar texto'}
                className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-black/10 bg-white text-black transition-colors hover:bg-neutral-50"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            )}
          </div>

          {m.description && (
            // v5.0.0 — descrição expande ao clicar para mostrar texto completo.
            <div
              className={`text-[11px] text-[hsl(var(--aff-muted-foreground))] [&_p]:m-0 cursor-pointer transition-all ${
                expanded ? '' : 'line-clamp-2'
              }`}
              onClick={() => setExpanded((v) => !v)}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.description) }}
              title={expanded ? 'Clique para recolher' : 'Clique para ver mais'}
            />
          )}

          {/* v5.0.0 — indicação dos programas vinculados ao material. */}
          {renderProgramChips()}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {/* Compartilhar no WhatsApp — disponível também para material texto. */}
          <button
            type="button"
            onClick={handleShareWhatsApp}
            title="Compartilhar no WhatsApp"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2.5 py-1 text-[11px] font-medium text-black transition-colors hover:bg-neutral-50"
          >
            <Share2 className="h-3.5 w-3.5" />
            Compartilhar
          </button>

          {m.file_url && (
            <>
              {/* "Abrir" agora abre o preview interno (sem link externo). */}
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                title="Abrir preview do material"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2.5 py-1 text-[11px] font-medium text-black transition-colors hover:bg-neutral-50"
              >
                <Maximize2 className="h-3.5 w-3.5" />
                Abrir
              </button>
              <a
                href={m.file_url}
                download
                title="Baixar material"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2.5 py-1 text-[11px] font-medium text-black transition-colors hover:bg-neutral-50"
              >
                <Download className="h-3.5 w-3.5" />
                Baixar
              </a>
            </>
          )}
        </div>
      </div>

      {/* Preview interno da mídia. */}
      <ImageLightbox
        open={previewOpen && !!mediaItem}
        onClose={() => setPreviewOpen(false)}
        mediaItems={mediaItem ? [mediaItem] : []}
        caption={plainText || m.title}
      />
    </div>
  );
}
